// Команда: люди, ставки и «к выплате» по явкам с кассы.
// Приход отмечается PIN-входом, уход — «Я ухожу» на экране PIN.
import { useState } from 'react';
import { useAskSheet, useNote } from './ui/ask';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AttendanceReport, LocationsList, PERMISSION_GROUPS, PERMISSION_INFO, PermissionKey, RolesList, StaffList, type PermissionMap, type StaffAssignment } from '@dastarhan/contracts';
import { formatMoney } from '@dastarhan/core';
import { downloadCsv, tg } from './csv';

const API = '/api/v1';
const dstr = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hm = (min: number) => `${Math.floor(min / 60)} ч ${min % 60} м`;

export function TeamScreen({ token, isOwner }: { token: string; isOwner: boolean }) {
  const { ask, sheet } = useAskSheet();
  const { note, noteView } = useNote();
  const qc = useQueryClient();
  const h = { Authorization: `Bearer ${token}` };
  const jh = { ...h, 'Content-Type': 'application/json' };
  const [from, setFrom] = useState(dstr(new Date(Date.now() - 6 * 86400_000)));
  const [to, setTo] = useState(dstr(new Date()));

  const staff = useQuery({
    queryKey: ['staff'],
    queryFn: async () => StaffList.parse(await (await fetch(`${API}/staff`, { headers: h })).json()),
  });
  const att = useQuery({
    queryKey: ['att', from, to],
    queryFn: async () =>
      AttendanceReport.parse(await (await fetch(`${API}/staff/attendance?from=${from}&to=${to}`, { headers: h })).json()),
  });
  const locations = useQuery({
    queryKey: ['locations'],
    queryFn: async () => LocationsList.parse(await (await fetch(`${API}/menu/locations`, { headers: h })).json()),
  });
  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: async () => RolesList.parse(await (await fetch(`${API}/staff/roles`, { headers: h })).json()),
  });
  const [draft, setDraft] = useState<Record<string, PermissionMap>>({});
  const saveRole = useMutation({
    mutationFn: (b: { id: string; permissions: PermissionMap }) =>
      fetch(`${API}/staff/roles/${b.id}`, {
        method: 'PATCH', headers: jh, body: JSON.stringify({ permissions: b.permissions }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['roles'] }),
  });
  const createRole = useMutation({
    mutationFn: (name: string) =>
      fetch(`${API}/staff/roles`, {
        method: 'POST', headers: jh,
        body: JSON.stringify({
          name,
          permissions: {
            'order.create': 'allowed',
            'order.item.remove': 'elevated_pin',
            'order.cancel': 'denied',
            'order.refund': 'elevated_pin',
            'order.discount': 'elevated_pin',
            'order.pay': 'allowed',
            'cash.shift.close': 'allowed',
          },
        }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['roles'] }),
  });

  const [hire, setHire] = useState(false);
  const [hf, setHf] = useState({ fullName: '', pin: '', roleId: '', locationId: '', rate: '' });
  const [hireErr, setHireErr] = useState('');
  const createStaff = useMutation({
    mutationFn: async (b: object) => {
      const r = await fetch(`${API}/staff`, { method: 'POST', headers: jh, body: JSON.stringify(b) });
      if (!r.ok) throw new Error(((await r.json()) as { code?: string }).code ?? 'ERR');
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['staff'] });
      setHire(false);
    },
    onError: (e) => setHireErr((e as Error).message === 'PIN_TAKEN'
      ? 'Этот PIN уже занят на выбранной точке — придумайте другой.'
      : 'Не получилось создать сотрудника.'),
  });

  const [assignFor, setAssignFor] = useState<{ userId: string; fullName: string; assignments: StaffAssignment[] } | null>(null);
  const [af, setAf] = useState({ locationId: '', roleId: '' });
  const addAssignment = useMutation({
    mutationFn: (b: { userId: string; locationId: string; roleId: string }) =>
      fetch(`${API}/staff/${b.userId}/assignments`, {
        method: 'POST', headers: jh,
        body: JSON.stringify({ locationId: b.locationId, roleId: b.roleId }),
      }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['staff'] }); setAssignFor(null); },
  });
  const removeAssignment = useMutation({
    mutationFn: (aid: string) =>
      fetch(`${API}/staff/assignments/${aid}`, { method: 'DELETE', headers: h }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['staff'] }); setAssignFor(null); },
  });

  const patch = useMutation({
    mutationFn: ({ id, ...b }: { id: string; hourlyRate?: number | null; isActive?: boolean }) =>
      fetch(`${API}/staff/${id}`, { method: 'PATCH', headers: jh, body: JSON.stringify(b) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['staff'] });
      void qc.invalidateQueries({ queryKey: ['att'] });
    },
  });

  if (staff.isPending) return <p className="empty">Загружаем команду…</p>;
  if (staff.isError || !staff.data) return <p className="empty">Не удалось загрузить. Обновите страницу.</p>;

  const attBy = new Map((att.data?.rows ?? []).map((r) => [r.userId, r] as const));
  const payTotal = (att.data?.rows ?? []).reduce((s, r) => s + (r.pay ?? 0), 0);
  // чаевые показываются рядом с выплатой, но не складываются с ней:
  // это деньги гостя сотруднику, а не обязательство заведения
  const tipsTotal = (att.data?.rows ?? []).reduce((s, r) => s + (r.tips ?? 0), 0);

  return (
    <div className="team">
      <div className="menu-toolbar">
        <div className="row">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <span>—</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="row">
          {isOwner && (
            <button className="btn accent" onClick={() => {
              setHf({
                fullName: '', pin: '',
                roleId: roles.data?.rows[0]?.id ?? '',
                locationId: locations.data?.[0]?.id ?? '',
                rate: '',
              });
              setHireErr('');
              setHire(true);
            }}>+ Сотрудник</button>
          )}
          <span className="hint-inline">К выплате за период: <b className="money">{formatMoney(payTotal)}</b></span>
          {tipsTotal > 0 && (
            <span className="hint-inline">Чаевые: <b className="money">{formatMoney(tipsTotal)}</b></span>
          )}
          <button className="btn" onClick={() => {
            if (!att.data) return;
            downloadCsv(`команда_${from}_${to}.csv`, [
              ['Сотрудник', 'Часы', 'Ставка, ₸/ч', 'К выплате, ₸', 'Чаевые, ₸'],
              ...att.data.rows.map((r) => [
                r.fullName,
                +(r.minutes / 60).toFixed(2),
                r.hourlyRate === null ? '' : tg(r.hourlyRate),
                r.pay === null ? '' : tg(r.pay),
                tg(r.tips ?? 0),
              ]),
            ]);
          }}>Скачать CSV</button>
        </div>
      </div>

      <div className="rep-table team-table">
        <div className="rep-row head">
          <span>Сотрудник</span><span>Роль</span><span className="right">Ставка</span>
          <span className="right">Часы за период</span><span className="right">К выплате</span>
          <span className="right">Чаевые</span><span />
        </div>
        {staff.data.rows.map((u) => {
          const a = attBy.get(u.userId);
          return (
            <div key={u.userId} className={`rep-row ${u.isActive ? '' : 'muted'}`}>
              <span>
                {u.fullName}
                {a?.openNow && (a.minutes ?? 0) < 16 * 60 && (
                  <i className="on-shift" title="Сейчас на смене"> ● на месте</i>
                )}
                {/* Забытый уход: часы обрезаны потолком, но явку надо закрыть руками */}
                {a?.openNow && (a.minutes ?? 0) >= 16 * 60 && (
                  <i className="stale-shift" title="Явка не закрыта — часы считаются по потолку 16 ч">
                    {' ⚠ забыли отметить уход'}
                  </i>
                )}
                {!u.hasPin && !u.isOwner && <small className="hint-inline"> · без PIN</small>}
              </span>
              <span>
                {u.isOwner
                  ? 'Владелец'
                  : [...new Set(u.assignments.map((a) => a.roleName))].join(', ') || '—'}
                {!u.isOwner && isOwner && (
                  <button className="name-btn" style={{ marginLeft: 6 }} title="Назначения по точкам"
                    onClick={() => {
                      setAf({
                        locationId: locations.data?.[0]?.id ?? '',
                        roleId: roles.data?.rows[0]?.id ?? '',
                      });
                      setAssignFor({ userId: u.userId, fullName: u.fullName, assignments: u.assignments });
                    }}>✎</button>
                )}
              </span>
              <button className="price money right" title="Изменить ставку"
                onClick={() => void (async () => {
                  const a = await ask({
                    title: `Ставка «${u.fullName}»`,
                    sub: 'По ней считается зарплата за отработанные часы в отчёте по смене.',
                    effects: [
                      ['Сейчас', u.hourlyRate === null ? 'не задана' : `${formatMoney(u.hourlyRate)}/ч`],
                    ],
                    value: {
                      label: 'Ставка, ₸ в час',
                      initial: u.hourlyRate === null ? '' : String(u.hourlyRate / 100),
                      numeric: true,
                      allowEmpty: true,
                      hint: 'пусто — убрать ставку, часы считаться будут, деньги — нет',
                    },
                    confirmLabel: 'Сохранить',
                  });
                  if (!a) return;
                  const hourlyRate = a.value === '' ? null : Math.round(Number(a.value.replace(',', '.')) * 100);
                  if (hourlyRate === null || (Number.isFinite(hourlyRate) && hourlyRate >= 0)) {
                    patch.mutate({ id: u.userId, hourlyRate });
                  } else note('Ставка не может быть отрицательной', 'bad');
                })()}>
                {u.hourlyRate === null ? 'задать' : `${formatMoney(u.hourlyRate)}/ч`}
              </button>
              <span className="right">{a ? hm(a.minutes) : '—'}</span>
              <span className="money right">{a?.pay != null ? formatMoney(a.pay) : '—'}</span>
              {/* чаевые в той же строке: видно весь доход человека за период */}
              <span className="money right">{(a?.tips ?? 0) > 0 ? formatMoney(a!.tips) : '—'}</span>
              <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                {isOwner && (
                  <button className="btn small" title="Сменить PIN" onClick={() => void (async () => {
                    /* PIN — это вход на кассу. Старый перестаёт работать сразу,
                       поэтому новый нужно передать человеку до начала смены. */
                    const a = await ask({
                      title: `Новый PIN для «${u.fullName}»`,
                      sub: 'По нему сотрудник входит на кассе. Прежний PIN перестанет работать сразу — передайте новый до смены.',
                      effects: [
                        ['Сотрудник', u.fullName],
                        ['Открытая смена', 'не закроется — вход по новому PIN'],
                      ],
                      value: {
                        label: 'PIN — четыре цифры',
                        numeric: true,
                        pattern: { re: /^\d{4}$/, wrong: 'Ровно четыре цифры' },
                        placeholder: '0000',
                        hint: 'у каждого на точке свой — одинаковых не бывает',
                      },
                      danger: true,
                      confirmLabel: 'Сменить PIN',
                    });
                    if (!a) return;
                    const r = await fetch(`${API}/staff/${u.userId}`, {
                      method: 'PATCH', headers: jh, body: JSON.stringify({ pin: a.value }),
                    });
                    if (r.status === 409) note('Этот PIN уже занят на одной из точек сотрудника — выберите другой', 'bad');
                    else if (!r.ok) note('Не получилось сменить PIN', 'bad');
                    else {
                      void qc.invalidateQueries({ queryKey: ['staff'] });
                      note(`PIN «${u.fullName}» изменён — передайте его лично`);
                    }
                  })()}>PIN</button>
                )}
                {!u.isOwner && (
                  <button className="btn small" onClick={() => patch.mutate({ id: u.userId, isActive: !u.isActive })}>
                    {u.isActive ? 'Отключить' : 'Вернуть'}
                  </button>
                )}
                {isOwner && (
                  <button className="btn small ghost" title="Завершить все входы этого человека"
                    onClick={() => void (async () => {
                      const a = await ask({
                        title: `Завершить все входы «${u.fullName}»`,
                        sub: 'Нужно, если телефон потерян или человек уволился: со всех устройств его выбросит сразу.',
                        effects: [
                          ['Сотрудник', u.fullName],
                          ['Где выбросит', 'офис, касса, телефон официанта'],
                          ['PIN', 'останется прежним — войти сможет снова'],
                        ],
                        danger: true,
                        confirmLabel: 'Завершить входы',
                      });
                      if (!a) return;
                      const r = await fetch(`${API}/auth/logout-everywhere`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ userId: u.userId }),
                      });
                      note(r.ok ? 'Все входы завершены' : 'Не получилось — попробуйте ещё раз', r.ok ? 'ok' : 'bad');
                    })()}>Выйти везде</button>
                )}
              </span>
            </div>
          );
        })}
      </div>
      <p className="hint-inline" style={{ display: 'block', marginTop: 10 }}>
        Часы считаются по явкам: приход — PIN-вход на кассе, уход — кнопка «Я ухожу» на экране PIN.
      </p>

      <section className="menu-cat" style={{ marginTop: 16 }}>
        <header>
          <h2>Роли и права</h2>
          {isOwner && (
            <button className="btn small" disabled={createRole.isPending} onClick={() => void (async () => {
              const a = await ask({
                title: 'Новая роль',
                sub: 'Роль — это набор прав. Права отметите сразу после создания.',
                value: {
                  label: 'Как называется',
                  initial: 'Старший смены',
                  hint: 'так её увидят при назначении сотруднику',
                },
                confirmLabel: 'Создать роль',
              });
              if (a) createRole.mutate(a.value);
            })()}>+ Роль</button>
          )}
        </header>
        <div className="roles-grid">
          {(roles.data?.rows ?? []).map((role) => {
            const perms = draft[role.id] ?? role.permissions;
            const dirty = draft[role.id] !== undefined;
            return (
              <div key={role.id} className="role-card">
                <h3>{role.name}</h3>
                {/* Права сгруппированы: владелец читает сверху вниз
                    и понимает, что даёт. Плоский список из двадцати
                    строк никто не дочитывает до конца */}
                {PERMISSION_GROUPS.map((group) => {
                  const keys = PermissionKey.options.filter(
                    (k) => PERMISSION_INFO[k]?.group === group,
                  );
                  if (keys.length === 0) return null;
                  return (
                    <div key={group} className="perm-group">
                      <h4>{group}</h4>
                      {keys.map((k) => (
                        <label key={k} className="role-line perm-line">
                          <span className="perm-text">
                            <b>{PERMISSION_INFO[k]?.title ?? k}</b>
                            <i>{PERMISSION_INFO[k]?.hint ?? ''}</i>
                          </span>
                          <select value={perms[k] ?? 'denied'} disabled={!isOwner}
                            onChange={(e) => setDraft({
                              ...draft,
                              [role.id]: { ...perms, [k]: e.target.value as PermissionMap[string] },
                            })}>
                            <option value="allowed">можно</option>
                            <option value="elevated_pin">PIN старшего</option>
                            <option value="denied">нельзя</option>
                          </select>
                        </label>
                      ))}
                    </div>
                  );
                })}

                <label className="role-line">
                  <span>Лимит скидки, %</span>
                  <input inputMode="numeric" style={{ width: 64 }} disabled={!isOwner}
                    placeholder="∞"
                    value={role.discountLimitPct === null ? '' : String(role.discountLimitPct)}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\D/g, '').slice(0, 3);
                      const v = raw === '' ? null : Math.min(100, Math.max(1, Number(raw)));
                      void fetch(`${API}/staff/roles/${role.id}`, {
                        method: 'PATCH', headers: jh,
                        body: JSON.stringify({ discountLimitPct: v }),
                      }).then(() => void qc.invalidateQueries({ queryKey: ['roles'] }));
                    }} />
                </label>
                {isOwner && (
                  <button className="btn accent small" disabled={!dirty || saveRole.isPending}
                    onClick={() => saveRole.mutate(
                      { id: role.id, permissions: perms },
                      { onSuccess: () => setDraft(({ [role.id]: _, ...rest }) => rest) },
                    )}>
                    {dirty ? 'Сохранить' : 'Сохранено'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <p className="hint-inline" style={{ display: 'block', padding: '0 16px 14px' }}>
          {isOwner
            ? 'Права применяются при следующем PIN-входе кассира.'
            : 'Права ролей меняет владелец.'}
        </p>
      </section>

      <TeamDialogs
        hire={hire} setHire={setHire} hf={hf} setHf={setHf} hireErr={hireErr}
        createStaff={createStaff}
        roles={roles.data?.rows ?? []}
        locations={locations.data ?? []}
        assignFor={assignFor} setAssignFor={() => setAssignFor(null)}
        af={af} setAf={setAf}
        addAssignment={addAssignment} removeAssignment={removeAssignment}
      />
      {sheet}
      {noteView}
    </div>
  );
}

function TeamDialogs({ hire, setHire, hf, setHf, hireErr, createStaff, roles, locations,
  assignFor, setAssignFor, af, setAf, addAssignment, removeAssignment }: {
  hire: boolean; setHire: (v: boolean) => void;
  hf: { fullName: string; pin: string; roleId: string; locationId: string; rate: string };
  setHf: (v: TeamDialogsProps['hf']) => void;
  hireErr: string;
  createStaff: { mutate: (b: object) => void; isPending: boolean };
  roles: { id: string; name: string }[];
  locations: { id: string; name: string }[];
  assignFor: { userId: string; fullName: string; assignments: StaffAssignment[] } | null;
  setAssignFor: (v: null) => void;
  af: { locationId: string; roleId: string };
  setAf: (v: { locationId: string; roleId: string }) => void;
  addAssignment: { mutate: (b: { userId: string; locationId: string; roleId: string }) => void; isPending: boolean };
  removeAssignment: { mutate: (aid: string) => void };
}) {
  return (
    <>
      {hire && (
        <div className="dialog-backdrop" role="dialog" aria-modal="true" onClick={() => setHire(false)}>
          <div className="office-dialog" onClick={(e) => e.stopPropagation()}>
            <h2>Новый сотрудник</h2>
            <label>Имя
              <input autoFocus value={hf.fullName}
                onChange={(e) => setHf({ ...hf, fullName: e.target.value })} />
            </label>
            <div className="row">
              <label>PIN для кассы
                <input inputMode="numeric" maxLength={4} value={hf.pin} style={{ width: 90 }}
                  onChange={(e) => setHf({ ...hf, pin: e.target.value.replace(/\D/g, '').slice(0, 4) })} />
              </label>
              <label>Ставка, ₸/ч
                <input inputMode="decimal" value={hf.rate} style={{ width: 100 }}
                  placeholder="не обяз." onChange={(e) => setHf({ ...hf, rate: e.target.value })} />
              </label>
            </div>
            <label>Роль
              <select value={hf.roleId} onChange={(e) => setHf({ ...hf, roleId: e.target.value })}>
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </label>
            <label>Точка
              <select value={hf.locationId} onChange={(e) => setHf({ ...hf, locationId: e.target.value })}>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </label>
            {hireErr && <p className="dlg-err">{hireErr}</p>}
            <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn" onClick={() => setHire(false)}>Отмена</button>
              <button className="btn accent"
                disabled={hf.fullName.trim().length < 2 || hf.pin.length !== 4 || !hf.roleId || !hf.locationId || createStaff.isPending}
                onClick={() => {
                  const rate = hf.rate.trim() === '' ? null : Math.round(Number(hf.rate.replace(',', '.')) * 100);
                  createStaff.mutate({
                    fullName: hf.fullName.trim(),
                    pin: hf.pin,
                    roleId: hf.roleId,
                    locationId: hf.locationId,
                    ...(rate !== null && Number.isFinite(rate) && rate >= 0 ? { hourlyRate: rate } : {}),
                  });
                }}>
                Создать
              </button>
            </div>
          </div>
        </div>
      )}

      {assignFor && (
        <div className="dialog-backdrop" role="dialog" aria-modal="true" onClick={() => setAssignFor(null)}>
          <div className="office-dialog" onClick={(e) => e.stopPropagation()}>
            <h2>Назначения: {assignFor.fullName}</h2>
            {assignFor.assignments.length === 0 && (
              <p className="dlg-line">Назначений нет — без них PIN-вход на кассу не сработает.</p>
            )}
            {assignFor.assignments.map((a) => (
              <div key={a.assignmentId} className="row" style={{ justifyContent: 'space-between' }}>
                <span className="dlg-line">{a.locationName} · <b>{a.roleName}</b></span>
                <button className="btn small" onClick={() => removeAssignment.mutate(a.assignmentId)}>Убрать</button>
              </div>
            ))}
            <div className="row">
              <select value={af.locationId} onChange={(e) => setAf({ ...af, locationId: e.target.value })}>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <select value={af.roleId} onChange={(e) => setAf({ ...af, roleId: e.target.value })}>
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <button className="btn small" disabled={!af.locationId || !af.roleId || addAssignment.isPending}
                onClick={() => addAssignment.mutate({ userId: assignFor.userId, ...af })}>
                + Назначить
              </button>
            </div>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setAssignFor(null)}>Закрыть</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
type TeamDialogsProps = Parameters<typeof TeamDialogs>[0];

// Подписи прав переехали в PERMISSION_INFO рядом с их ключами:
// список растёт, и словарь в другом файле однажды отстанет
